import { entryListToIndexes, entryListToMap, entryMapToEntryIndex } from 'helpers/general';
import * as constants from 'actions/constants';
import { EntryType } from 'actions/general';
import { removeEntryFromArrayById } from 'helpers/array';
import { Post } from 'types/post';

const defaultState: PostReducer = {
  posts: new Map(),
  entryDateIndex: [],
  loading: false,
};

export interface EntryDateIndex {
  date: number;
  entryId: string;
}

export interface PostReducer {
  posts: Map<string, Post>;
  entryDateIndex: EntryDateIndex[];
  loading: boolean;
}

export default function postReducer(state = defaultState, action: any): PostReducer {
  switch (action.type) {
    case constants.GET_LATEST_POSTS_TRY:
      return { ...state, loading: true };

    case constants.GET_LATEST_POSTS_ERROR:
      return { ...state, loading: false };

    case constants.GET_LATEST_POSTS_RESPONSE: {
      if (action.response.success) {
        const newPosts = entryListToMap<Post>(action.response.entries, state.posts);
        return {
          ...state,
          posts: newPosts,
          entryDateIndex: entryMapToEntryIndex<Post>(newPosts),
          loading: false,
        };
      }
      return { ...state, loading: false };
    }

    case constants.DELETE_POST_RESPONSE: {
      if (action.entryType !== EntryType.POST || !action.response.success) return state;

      const updatedEntryDateIndex = removeEntryFromArrayById(state.entryDateIndex, action.entryId);
      const updatedPosts = new Map(state.posts);
      updatedPosts.delete(action.entryId);
      return { ...state, posts: updatedPosts, entryDateIndex: updatedEntryDateIndex };
    }

    case constants.CREATE_POST_RESPONSE:
    case constants.CREATE_IMAGE_POST_RESPONSE: {
      if (!action.response.success) return state;

      const updatedPosts = new Map(state.posts);
      updatedPosts.set(action.response.entry.id, action.response.entry);

      const newDateIndex = entryListToIndexes([action.response.entry], [...state.entryDateIndex]);
      return { ...state, posts: updatedPosts, entryDateIndex: newDateIndex };
    }

    case constants.GET_LATEST_POSTS_BY_USER_RESPONSE: {
      if (action.response.success) {
        const newPosts = entryListToMap<Post>(action.response.entries, state.posts);
        return { ...state, posts: newPosts, entryDateIndex: entryMapToEntryIndex<Post>(newPosts) };
      }
      return state;
    }

    case constants.GET_ENTRY_RESPONSE: {
      if (!action.response.success) return state;

      const updatedPosts = new Map(state.posts);
      if (action.response.status === 404) {
        if (!state.posts.has(action.entryId)) return state;

        updatedPosts.delete(action.entryId);
        const updatedEntryDateIndex = removeEntryFromArrayById(state.entryDateIndex, action.entryId);
        return { ...state, posts: updatedPosts, entryDateIndex: updatedEntryDateIndex };
      }

      updatedPosts.set(action.response.entry.id, action.response.entry);
      const newDateIndex = entryListToIndexes([action.response.entry], [...state.entryDateIndex]);
      return { ...state, posts: updatedPosts, entryDateIndex: newDateIndex };
    }

    case constants.LIKE_ENTRY_RESPONSE: {
      if (!action.response.success || action.entryType !== EntryType.POST) return state;

      const updatedPosts = new Map(state.posts);
      const post = updatedPosts.get(action.entryId);
      if (!post) return state;

      post.likesIds = { ...post.likesIds, [action.uid]: action.isLike };
      return { ...state, posts: updatedPosts };
    }

    case constants.CLEAR_OLD_DATA: {
      if (action.force && action.entryType === EntryType.POST) return defaultState;
      if (state.entryDateIndex.length < 30) return state;

      const newEntryDateIndex = state.entryDateIndex.sort((a, b) => b.date - a.date).slice(0, 30);
      const updatedPosts = new Map(state.posts);
      for (const entryIndex of newEntryDateIndex) {
        updatedPosts.delete(entryIndex.entryId);
      }
      return { ...state, posts: updatedPosts, entryDateIndex: newEntryDateIndex };
    }

    case constants.LOGOUT_RESPONSE:
      return defaultState;

    default:
      if (action?.response?.code === 'FORCE_LOGOUT_FRONTEND') return defaultState;
      return state;
  }
}
